/**
 * 공통 확인 다이얼로그 컴포넌트
 * 토스 스타일 중앙 모달 형태의 확인/취소 다이얼로그
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

export interface ConfirmDialogProps {
  /** 다이얼로그 표시 여부 */
  isOpen: boolean;
  /** 제목 */
  title: string;
  /** 설명 메시지 */
  message?: string;
  /** 확인 버튼 텍스트 (기본: "확인") */
  confirmText?: string;
  /** 취소 버튼 텍스트 (기본: "취소") */
  cancelText?: string;
  /** 확인 버튼 스타일 - destructive일 경우 빨간색 */
  variant?: "default" | "destructive";
  /** 확인 버튼 클릭 핸들러 */
  onConfirm: () => void;
  /** 취소 버튼 클릭 핸들러 */
  onCancel: () => void;
  /** 로딩 상태 (확인 버튼 비활성화) */
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = "확인",
  cancelText = "취소",
  variant = "default",
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmDialogProps) {
  // 애니메이션을 위한 내부 상태
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const rafIdRef = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // ESC 키 핸들러
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        onCancel();
      }
    },
    [isLoading, onCancel]
  );

  // 뒤로가기 이벤트 핸들러 (Android 백버튼 / 헤더 뒤로가기)
  const handleCloseTopModal = useCallback(
    (e: Event) => {
      if (!isLoading) {
        e.preventDefault(); // 이벤트 처리됨을 알림
        onCancel();
      }
    },
    [isLoading, onCancel]
  );

  useEffect(() => {
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    if (isOpen) {
      setIsVisible(true);
      // 다음 프레임에서 애니메이션 시작
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      // ESC 키 이벤트 등록
      document.addEventListener("keydown", handleKeyDown);
      // 뒤로가기 이벤트 등록
      document.addEventListener("closeTopModal", handleCloseTopModal);
    } else {
      setIsAnimating(false);
      // 애니메이션 완료 후 숨김
      closeTimer = setTimeout(() => setIsVisible(false), 150);
    }

    // 단일 cleanup 함수
    return () => {
      if (closeTimer) clearTimeout(closeTimer);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("closeTopModal", handleCloseTopModal);
    };
  }, [isOpen, handleKeyDown, handleCloseTopModal]);

  if (!isVisible) return null;

  const handleBackdropClick = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // createPortal로 document.body에 렌더링하여
  // 부모의 transform이 fixed 포지셔닝에 영향을 주지 않도록 함
  return createPortal(
    <div
      data-testid="confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className={`fixed inset-0 z-50 flex items-center justify-center p-6 transition-colors duration-150 ${
        isAnimating ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={handleBackdropClick}
    >
      {/* 중앙 모달 컨테이너 */}
      <div
        ref={dialogRef}
        className={`w-full max-w-[280px] transform transition-all duration-150 ease-out ${
          isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
        onClick={handleContentClick}
      >
        {/* 카드 */}
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* 콘텐츠 영역 */}
          <div className="px-5 pb-4 pt-6">
            <h3
              id="confirm-dialog-title"
              className="text-center text-[17px] font-semibold text-gray-900"
            >
              {title}
            </h3>
            {message && (
              <p className="mt-2 whitespace-pre-line text-center text-[13px] text-gray-500 leading-relaxed">
                {message}
              </p>
            )}
          </div>

          {/* 버튼 영역 */}
          <div className="flex border-t border-gray-200">
            {/* 취소 버튼 */}
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-3.5 text-[16px] font-normal text-blue-500 transition-colors active:bg-gray-100 disabled:opacity-50"
            >
              {cancelText}
            </button>

            {/* 구분선 */}
            <div className="w-px bg-gray-200" />

            {/* 확인 버튼 */}
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 py-3.5 text-[16px] font-semibold transition-colors disabled:opacity-50 ${
                variant === "destructive"
                  ? "text-red-500 active:bg-red-50"
                  : "text-blue-500 active:bg-blue-50"
              }`}
            >
              {isLoading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
